import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Card, CardActions, CardContent, Rating, Typography, Dialog, ImageList, ImageListItem, Button } from '@mui/material';
import { StarBorder as StarBorderIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetReviewImagesQuery } from '../features/api/rest/apiReviewsImagesSlice';
import VotingReview from './VotingReview';
import ReviewReport from './ReviewReport';

function ReviewCard({ id, date, title, description, rating, owner, totScore, numVotes, ean }) {
  const formatDate = (dateToFormat) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateToFormat).toLocaleDateString('it-IT', options);
  };
  const { t } = useTranslation();

  const { data: images } = useGetReviewImagesQuery({ ean, rpk: id });

  const [seeImages, setSeeImages] = useState(false);
  const [nameImage, setNameImage] = useState('');
  const [fullImage, setFullImage] = useState(false);

  const listImageAdded = images?.map((image) => (
    <ImageListItem
      key={image?.id}
      onClick={() => {
        setFullImage(true);
        setNameImage(image.image);
      }}
    >
      <img style={{ height: '20px' }} srcSet={`${image.image}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`} src={`${image.image}?w=164&h=164&fit=crop&auto=format`} alt={image.image.split('/').pop()} loading="lazy" />
    </ImageListItem>
  ));

  return (
    <Card sx={{ border: '1px solid #d3d4d5', my: 4 }} elevation={0} data-testid="ReviewCard">
      <CardContent>
        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          {owner}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rating name="read-only" value={rating} readOnly emptyIcon={<StarBorderIcon />} />
          <Typography variant="h6" component="div" sx={{ ml: 2, fontSize: 17, fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: 13 }}>
          {formatDate(date)}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </CardContent>
      <CardActions>
        <VotingReview id={id} totScore={totScore} numVotes={numVotes} />
        <ReviewReport reviewId={id} owner={owner} />
      </CardActions>
      {images && images.length > 0 && <Button sx={{ ml: 2, mb: 2 }} variant="contained" onClick={() => setSeeImages(!seeImages)}>{`${t(seeImages ? 'reviews.images.closeImages' : 'reviews.images.seeImages')}`}</Button>}
      {seeImages && (
        <ImageList sx={{ height: 450, m: 1 }} cols={3} rowHeight={150} gap={10}>
          {listImageAdded}
        </ImageList>
      )}
      <Dialog
        open={fullImage}
        onClose={() => {
          setFullImage(false);
          setNameImage('');
        }}
        fullWidth
        maxWidth="sm"
        fullScreen={fullImage}
      >
        <CancelIcon
          sx={{ position: 'absolute', top: '2%', left: '96%', cursor: 'pointer', color: 'red', fontSize: '2rem' }}
          onClick={() => {
            setFullImage(false);
            setNameImage('');
          }}
        />
        <img src={`${nameImage}`} alt={nameImage.split('/').pop()} loading="lazy" data-testid="FullImage" />
      </Dialog>
    </Card>
  );
}

ReviewCard.propTypes = {
  id: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  rating: PropTypes.number.isRequired,
  owner: PropTypes.string.isRequired,
  totScore: PropTypes.number.isRequired,
  numVotes: PropTypes.number.isRequired,
  ean: PropTypes.string.isRequired,
};

export default ReviewCard;
